import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: grid;
  place-items: center;
  background-color: var(--color-background);
  padding: clamp(20px, 4vw, 48px);
`;

const LoginCard = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 0.9fr) minmax(320px, 1fr);
  width: min(100%, 980px);
  min-height: 560px;
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid var(--color-border);
  overflow: hidden;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    min-height: auto;
  }
`;

const BrandPanel = styled.aside`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: var(--space-lg);
  background: var(--color-primary-dark);
  color: white;
  padding: clamp(32px, 5vw, 56px);

  @media (max-width: 820px) {
    display: none;
  }
`;

const BrandMark = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 2px solid var(--color-accent);
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 2px solid rgba(0, 24, 206, 0.75);
    background: var(--color-periwinkle);
    box-shadow: inset 0 0 0 8px white;
  }
`;

const BrandEyebrow = styled.p`
  margin: 36px 0 12px;
  color: rgba(255, 255, 255, 0.56);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.22em;
  text-transform: uppercase;
`;

const BrandTitle = styled.h2`
  margin: 0;
  max-width: 360px;
  color: white;
  font-family: var(--font-serif);
  font-size: clamp(2.6rem, 4.4vw, 3.8rem);
  font-weight: var(--font-weight-semibold);
  line-height: 1.02;
`;

const BrandList = styled.div`
  display: grid;
  gap: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  padding-top: 28px;
`;

const BrandListTitle = styled.p`
  margin: 0 0 5px;
  color: rgba(255, 255, 255, 0.46);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const BrandListText = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: var(--font-size-sm);
`;

const FormPanel = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(32px, 5vw, 56px);
  background: var(--color-surface);
`;

const Title = styled.h1`
  margin-bottom: ${props => props.theme.spacing.sm};
  color: var(--color-primary);
  font-family: var(--font-serif);
  font-size: clamp(2.25rem, 5vw, 3.25rem);
  font-weight: ${props => props.theme.fontWeight.semibold};
  line-height: 1.05;
`;

const Subtitle = styled.p`
  margin-bottom: ${props => props.theme.spacing.lg};
  color: var(--color-text-secondary);
  font-size: ${props => props.theme.fontSize.sm};
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const Label = styled.label`
  color: var(--color-text-secondary);
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.semibold};
`;

const Input = styled.input`
  padding: ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.md};
  transition: border-color ${props => props.theme.transitions.default};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
    box-shadow: 0 0 0 4px rgba(0, 24, 206, 0.08);
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textMuted};
  }
`;

const Button = styled.button`
  background: var(--color-periwinkle);
  color: white;
  border: none;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.md};
  font-weight: ${props => props.theme.fontWeight.medium};
  cursor: pointer;
  transition: background-color ${props => props.theme.transitions.default};
  
  &:hover:not(:disabled) {
    background: var(--color-accent);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  font-size: ${props => props.theme.fontSize.sm};
  margin-top: ${props => props.theme.spacing.xs};
  text-align: center;
`;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await login(email, password);
    } catch (error) {
      setError('Invalid email or password. Please try again.');
    }
    
    setLoading(false);
  };

  return (
    <LoginContainer>
      <LoginCard>
        <BrandPanel>
          <div>
            <BrandMark aria-hidden="true" />
            <BrandEyebrow>Collector&apos;s Eye</BrandEyebrow>
            <BrandTitle>Private admin console.</BrandTitle>
          </div>

          <BrandList>
            <div>
              <BrandListTitle>Artists</BrandListTitle>
              <BrandListText>Review accounts, access, invites, and onboarding status.</BrandListText>
            </div>
            <div>
              <BrandListTitle>Support</BrandListTitle>
              <BrandListText>Track collector and artist requests from the same console.</BrandListText>
            </div>
          </BrandList>
        </BrandPanel>

        <FormPanel>
          <div>
            <Title>Admin access</Title>
            <Subtitle>Sign in to manage artists, invitations, and platform support.</Subtitle>

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                  disabled={loading}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={loading}
                />
              </FormGroup>

              <Button type="submit" disabled={loading}>
                {loading ? 'Signing in...' : 'Enter Console'}
              </Button>

              {error && <ErrorMessage>{error}</ErrorMessage>}
            </Form>
          </div>
        </FormPanel>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;
